/* =====================================================================
   mk-portal-nav.js  (Shopify theme asset)  -  window.MKPortalNav
   MK Supplements customer portal (Skio Customer Portal v3, /a/account/*)

   Our own menu, in place of Skio's navbar:
     sidebar (>= 1024 px): Subscriptions, Upcoming orders, Add products,
       Order history, Rewards, Support, Account, Log out;
     icon rail (768-1023 px), short labels: Subscriptions, Upcoming, Add,
       History, Rewards, Support, Account, Log out;
     phones (< 768 px): a bottom tab bar
       Subscriptions, Upcoming, Add, Rewards, More; "More" opens a sheet with
       Order history, Support, Account, Log out (sheet items always use the
       long label).
     r10 (1.2.0): "Add products" (short "Add", icon a bag with a plus) exists
     only while the catalogue is on (MKPortal.catalogEnabled(): the loader's
     mk_portal_catalog exactly true and the home module on) and is hidden while
     MKPortalHome.catalogEnabled === false (the home module's own switch). With
     it gone the menu is r9.1's: Order history back in the phone bar
     (Subscriptions, Upcoming, History, Rewards, More; More = Support, Account,
     Log out).
     R11 (1.3.0): customers without a live subscription (never subscribed,
     or every subscription canceled) get their own order, handed in by the
     add-on mk-portal-nonsub.js (window.MKPortalNonsub, which exists only
     while the loader's mk_portal_nonsub is true; without it nothing of this
     runs and the menu is 1.2.0's): Order history, Rewards, Subscriptions,
     Support, Account, Log out; no Upcoming orders and no Add products (both
     still open from a link); phones: History, Rewards, Subscriptions,
     Support, More (More = Account, Log out). On a fresh sign-in (no answer
     yet, nothing remembered for this tab) the menu is mounted pending
     (data-mk-order="pending", aria-busy, items hidden; the look is the
     add-on's stylesheet) until the add-on answers, ORDER_WAIT_MS at most, so
     it never reorders in front of the customer; no answer by then: the usual
     order. A later answer (a slow read, or the customer subscribed) sets the
     order it calls for; Log out forgets it.
     R11 (1.4.0): "Returns" (icon a curved arrow back), the last item of the
     list (Jim, 2026-10-27: "Returns needs to be at the bottom of the nav
     list"; Log out stays apart at the foot), for every customer, only while
     the returns module runs (window.MKPortalReturns exists only while the
     loader's mk_portal_returns is true and the core's returnsEnabled();
     without it nothing of this runs and the menu is 1.3.0's): sidebar and
     rail after Account; phones: the last row of the More sheet, above Log
     out (subscribers: Order history, Support, Account, Returns, Log out;
     customers without a live subscription: Account, Returns, Log out). Its
     screens ('returns', 'return') live over Order history: the item is
     current while one is open.
   No logo in the menu at any width (Jim, 2026-10-11, CR-1): the store
   header above the portal already shows the MK logo. The loader still
   hands MKPortalConfig.logo / --mk-logo-* to the page for the login card
   and for Skio's own menu (App Shell styled, the fail-closed state); this
   file does not use them.
   Loaded by snippets/mk-portal.liquid (defer, right after the core) when
   the switch mk_portal_nav is on. Needs window.MKPortal (mk-portal-core.js).
   Styles: assets/mk-portal-nav.css (built from src/nav.css).

   FAIL CLOSED
   - No menu flash (batch 3): the loader's gate script puts class
     "mk-nav-pending" on <html> before the first paint, which keeps Skio's
     navbar from being drawn (mk-portal-nav.css). This file removes it as
     soon as our menu is ready (ours shows in the same frame) and on every
     failure or give-up (Skio's menu shows); if this file never runs, the
     gate script removes it itself after a timeout.
   - <nav id="mk-nav"> is mounted as the sibling right before Skio's
     [data-skio-name="navbar"]. Only after that succeeded does this file put
     class "mk-nav-ready" on <html>, which hides Skio's navbar (display:
     none; it stays in the DOM, React keeps running it, and its buttons stay
     clickable by script) and shows ours. Any failure removes the class
     first: Skio's own menu (App Shell styled) is back in the same frame.
   - No mount unless Skio's navbar and its logout button are there (our
     "Log out" only ever presses Skio's own button).
   - No swap unless mk-portal-nav.css is applied (its sentinel
     --mk-nav-css: 1 on #mk-nav): a missing stylesheet would otherwise show
     both menus and squeeze the page.
   - More than 3 re-mounts within 10 s: give up for this page view.
   - Text-only DOM (MKPortal.el / svg), no network, no storage writes; it
     only reads sessionStorage._skioPreviousPath (Skio's own key) to choose
     between two correct ways to reach the Subscriptions page.

   NAVIGATION
   - Skio's own hidden control where one exists on the current route
     (client-side, fast): navbar-back-button, navbar-orders-button,
     navbar-profile-button, loyalty-link-banner. If the URL has not reached
     the target 4 s later, or the control is missing: full page load. Never
     history.pushState (Next.js would not change the page).
   - Upcoming orders: on the subscriptions page (the start page) the screen
     opens at once on the same page, no reload: MKPortalHome.openUpcoming()
     (the home module owns that screen and adds the #upcoming history entry
     with Next's own router state, so Back from another page lands on the
     right page); location.hash = 'upcoming' only when that function is
     missing. From another page (batch 3, faster): where Skio has a button
     that leads to the start page client-side (navbar-back-button, the same
     cases as the Subscriptions item), it is pressed and the screen is
     opened the moment the start page is there; otherwise, or if the start
     page is not reached within 4 s, a full load to
     /a/account/subscriptions#upcoming. The item is hidden while
     MKPortalHome.upcomingEnabled is false (the home module's own off switch).
   - Add products (r10): exactly like Upcoming orders, for the screen
     'add-products': on the start page MKPortalHome.openCatalog() (location.hash
     = 'add-products' only when that function is missing); from another page
     Skio's own button to the start page, then the screen there; else a full
     load to /a/account/subscriptions#add-products. Focus on the item or the
     mouse over it calls MKPortalHome.prefetchCatalog() when the home module has
     it (read on intent; at most once a second from here, the home module
     caches).
   - Returns (R11): on Order history MKPortalReturns.open() (the returns module
     owns the screen and its #returns history entry); from another page Skio's
     own navbar Order history button, then the screen there; else a full load
     to /a/account/orders#returns. Order history while a returns screen is open
     asks MKPortalReturns.close().
   - Support: https://mksupps.com/pages/contact-us (a plain link, same tab)
     until a Support module registers window.MKPortalSupport; then
     location.hash = 'support'.
   - Plain primary clicks only: Cmd / Ctrl / Shift / middle click and
     "open in new tab" follow the real href.

   SCREENS (shared contract with the home module and the future Support module)
   - document.documentElement.dataset.mkScreen = '' | 'upcoming' | 'support' |
     'add-products' (r10), announced with the event 'mk-portal:screen'
     {screen}. This file only reads it: the matching item is shown as current
     (the brown pill; on phones the More button too when the item lives in the
     sheet); choosing the page's own item while a screen is open asks the owner
     to close it (MKPortalHome.closeUpcoming() / closeCatalog() /
     MKPortalSupport.close()). One screen at a time: choosing Upcoming while
     Add products is open (or the reverse) calls the other opener, and the home
     module closes its open screen first.

   API (only with the gate on)
     MKPortalNav.version
     MKPortalNav.state() -> {status: 'idle'|'waiting'|'ready'|'failed', active,
                             items: [keys], layout: 'sidebar'|'rail'|'bar', sheetOpen,
                             pending: <html> still has mk-nav-pending,
                             bar: [keys of the phone bar's items now], sheet: [keys of the
                             More sheet's items now] (r10; layout-independent),
                             order: 'default'|'nonsub'|'pending' (R11; 'default' without the add-on)}
     MKPortalNav.navigate(url)   full page load (location.assign; tests replace it)
     MKPortalNav.refresh()       recompute the current item
   Events: document 'mk-portal:nav' {state: 'ready' | 'gone'}
   ===================================================================== */
(function () {
  'use strict';
  if (window.MKPortalNav) return;
  var M = window.MKPortal;
  /* the menu cannot start: show Skio's own menu now (the loader's pending class hides it) */
  function unpend() { try { document.documentElement.classList.remove('mk-nav-pending'); } catch (e) { /* ignore */ } }
  if (!M || !M.__core || !M.on()) { unpend(); return; }
  var CONFIG = (window.MKPortalConfig && typeof window.MKPortalConfig === 'object') ? window.MKPortalConfig : {};
  var MODULES = (CONFIG.modules && typeof CONFIG.modules === 'object') ? CONFIG.modules : {};
  if (MODULES.nav === false) { unpend(); return; }

  var VERSION = '1.4.0';   /* 1.0.1 (2026-10-11): no logo in the menu (CR-1);
                              1.1.0 (2026-10-12, batch 3): no menu flash (ends the loader's mk-nav-pending);
                              Upcoming from another page through Skio's own button to the start page;
                              1.1.1 (2026-10-15, r7): the rail query is '(max-width: 1023.98px)' like the bar's
                              767.98px and the stylesheets (a fractional width under browser zoom, 1023-1024 px,
                              no longer gets the sidebar while the page uses its tablet layout);
                              1.2.0 (2026-10-18, r10): "Add products" (short "Add", 3rd; screen 'add-products'),
                              Order history to the top of the phone sheet while it is there, sheet items with
                              their long label, prefetch on intent (MKPortalHome.prefetchCatalog);
                              1.3.0 (2026-10-27, R11): the order for customers without a live subscription,
                              from the add-on (MKPortalNonsub), with the pending frame on a fresh sign-in;
                              nothing changes without the add-on;
                              1.4.0 (2026-10-27, R11): "Returns", the list's last item (above Log out), while the
                              returns module runs (MKPortalReturns); nothing changes without it */
  var SUPPORT_URL = 'https://mksupps.com/pages/contact-us';
  var PAGES = { subscriptions: 1, subscription: 1, loyalty: 1, orders: 1, profile: 1 };
  /* the query keys Skio's own router carries from page to page (editor preview); nothing else */
  var PREVIEW_KEYS = ['customerPortalPreview', 'forcePreview', 'forceCpv2', 'hostname', 'hmac', 'nonce', 'path_prefix'];
  var FALLBACK_MS = 4000;     /* native navigation must reach the target within this, else full load */
  var WAIT_MS = 15000;        /* wait this long for Skio's navbar */
  var MAX_REMOUNTS = 3;       /* within REMOUNT_WINDOW_MS, then give up for this page view */
  var REMOUNT_WINDOW_MS = 10000;
  var LOGOUT_GUARD_MS = 5000;
  var WARM_MIN_MS = 1000;     /* r10: at most one prefetchCatalog() call per second from the menu */

  var ICON = {
    subscriptions: ['M3 12a9 9 0 0 1 15.5-6.2L21 8', 'M21 3v5h-5', 'M21 12a9 9 0 0 1-15.5 6.2L3 16', 'M3 21v-5h5'],
    upcoming: ['M8 2v4', 'M16 2v4', 'M3 9h18', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z'],
    'add-products': ['M5.5 8h13l-1 12.2a2 2 0 0 1-2 1.8h-7a2 2 0 0 1-2-1.8z', 'M9 8V6.5a3 3 0 0 1 6 0V8', 'M12 11.5v6', 'M9 14.5h6'],
    orders: ['M21 8l-9-5-9 5v8l9 5 9-5z', 'M3 8l9 5 9-5', 'M12 13v8'],
    returns: ['M9 14L4 9l5-5', 'M4 9h10.5a5.5 5.5 0 0 1 0 11H11'],
    rewards: ['M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9z'],
    support: ['M21 11.5a8.4 8.4 0 0 1-8.5 8.3 9 9 0 0 1-3.6-.7L4 20.5l1.4-4.2A8 8 0 0 1 4 11.5a8.4 8.4 0 0 1 8.5-8.3 8.4 8.4 0 0 1 8.5 8.3z', 'M8.5 10.5h8M8.5 13.8h5'],
    account: ['M8 8a4 4 0 1 0 8 0a4 4 0 1 0-8 0', 'M4 21a8 8 0 0 1 16 0'],
    logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
    more: ['M4 7h16', 'M4 12h16', 'M4 17h16'],
    close: ['M6 6l12 12', 'M18 6L6 18']
  };

  /* key, slot (bar = phone tab bar; more = phone "More" sheet), labels, target. r10: Order history is in the sheet (at its
     top) while "Add products" is in the bar, else in the bar as in r9.1 (slotOf) */
  var ALL_ITEMS = [
    { key: 'subscriptions', slot: 'bar', label: 'Subscriptions', path: '/subscriptions' },
    { key: 'upcoming', slot: 'bar', label: 'Upcoming orders', short: 'Upcoming', path: '/subscriptions', hash: 'upcoming' },
    { key: 'add-products', slot: 'bar', label: 'Add products', short: 'Add', path: '/subscriptions', hash: 'add-products' },
    { key: 'orders', slot: 'more', label: 'Order history', short: 'History', path: '/orders', barWithoutAdd: true },
    { key: 'rewards', slot: 'bar', label: 'Rewards', path: '/loyalty' },
    { key: 'support', slot: 'more', label: 'Support', url: SUPPORT_URL },
    { key: 'account', slot: 'more', label: 'Account', path: '/profile' },
    { key: 'returns', slot: 'more', label: 'Returns', path: '/orders', hash: 'returns' },
    { key: 'logout', slot: 'more', label: 'Log out', button: true }
  ];
  var PAGE_ITEM = { subscriptions: 'subscriptions', subscription: 'subscriptions', orders: 'orders', loyalty: 'rewards', profile: 'account' };
  var ITEM_PAGE = { subscriptions: 'subscriptions', upcoming: 'subscriptions', 'add-products': 'subscriptions', orders: 'orders', returns: 'orders', rewards: 'loyalty', account: 'profile' };
  /* the screens of the home module: item key = screen name, with the owner's opener and closer */
  var HOME_SCREENS = { upcoming: { open: 'openUpcoming', close: 'closeUpcoming' }, 'add-products': { open: 'openCatalog', close: 'closeCatalog' } };

  /* r10: the catalogue's switch (the loader's mk_portal_catalog, exactly true, and the home module on), from the core */
  function catalogSwitch() {
    try { if (typeof M.catalogEnabled === 'function') return M.catalogEnabled() === true; } catch (e) { return false; }
    return false;
  }
  /* R11 (1.4.0): the returns module (window.MKPortalReturns, only while the loader's mk_portal_returns is true and the
     core says returnsEnabled()); it owns the screens 'returns' and 'return' over Order history */
  var RET = window.MKPortalReturns && typeof window.MKPortalReturns.open === 'function' &&
    typeof window.MKPortalReturns.close === 'function' ? window.MKPortalReturns : null;
  var RETURN_SCREENS = { returns: 1, 'return': 1 };
  /* "Upcoming orders" exists only where its screen exists (home module on, and no separate off switch); r10: "Add
     products" only with the catalogue switch on; R11: "Returns" only with the returns module */
  var ITEMS = ALL_ITEMS.filter(function (it) {
    if (it.key === 'upcoming') return MODULES.home !== false && MODULES.upcoming !== false;
    if (it.key === 'add-products') return MODULES.home !== false && catalogSwitch();
    if (it.key === 'returns') return !!RET;
    return true;
  });
  function itemDef(key) { for (var i = 0; i < ITEMS.length; i++) if (ITEMS[i].key === key) return ITEMS[i]; return null; }
  /* the home module's own off switch for the Upcoming screen (DECISION_UPCOMING.enabled) */
  function upcomingOff() { var H = window.MKPortalHome; return !!(H && H.upcomingEnabled === false); }
  /* r10: the home module's own off switch for the catalogue (DECISION_CATALOG.enabled) */
  function catalogHidden() { var H = window.MKPortalHome; return !!(H && H.catalogEnabled === false); }
  function usable(key) {
    return !!itemDef(key) && !(key === 'upcoming' && upcomingOff()) && !(key === 'add-products' && catalogHidden());
  }
  /* r10: where an item lives on phones now: Order history takes the bar slot back when "Add products" is not there */
  function slotOf(it) {
    if (!it) return null;
    if (it.barWithoutAdd) return usable('add-products') ? 'more' : 'bar';
    return it.slot;
  }

  /* R11 (1.3.0): the order for customers without a live subscription (see the header). Only with the add-on */
  var NS = window.MKPortalNonsub && typeof window.MKPortalNonsub.kindNow === 'function' &&
    typeof window.MKPortalNonsub.kind === 'function' ? window.MKPortalNonsub : null;
  var ORDER_WAIT_MS = 1200;   /* the pending frame lasts at most this long */
  var NONSUB_ORDER = [['orders', 'bar'], ['rewards', 'bar'], ['subscriptions', 'bar'], ['support', 'bar'], ['account', 'more'], ['logout', 'more']];
  if (RET) NONSUB_ORDER.splice(NONSUB_ORDER.length - 1, 0, ['returns', 'more']);   /* R11 (1.4.0): Returns last, above Log out */
  var DEFAULT_ITEMS = ITEMS;
  var NONSUB_ITEMS = NONSUB_ORDER.map(function (o) {
    var src = null, c = {}, k;
    ALL_ITEMS.forEach(function (it) { if (it.key === o[0]) src = it; });
    for (k in src) if (k !== 'barWithoutAdd') c[k] = src[k];
    c.slot = o[1];
    return c;
  });
  var order = 'default';      /* 'default' | 'nonsub' | 'pending' (the last two only with the add-on) */
  var orderWait = null;       /* the running wait for the add-on's answer: {timer} */
  function nonsubKind(k) { return k === 'never' || k === 'lapsed'; }
  if (NS) {
    var known = null;
    try { known = NS.kindNow(); } catch (e) { known = null; }
    if (nonsubKind(known)) { order = 'nonsub'; ITEMS = NONSUB_ITEMS; }
    else if (known !== 'subscriber') order = 'pending';
  }

  var api = {};
  var status = 'idle';
  var navNode = null;        /* built once per page load */
  var navEl = null;          /* mounted node */
  var skioNav = null;
  var observer = null;
  var waitSeq = 0;
  var remounts = [];
  var pending = null;        /* {key, target, timer, el} */
  var screenAfter = null;    /* {screen, at}: open this screen when the start page is reached (Upcoming from another page) */
  var sheetOpen = false;
  var sheetPath = null;      /* the address the sheet was opened on */
  var inerted = [];
  var lastActive = null;
  var announceKey = null;
  var logoutAt = 0;
  var mqBar = null, mqRail = null;
  var warmAt = 0;

  function html() { return document.documentElement; }
  function dispatch(state) {
    try { document.dispatchEvent(new CustomEvent('mk-portal:nav', { detail: { state: state } })); } catch (e) { /* ignore */ }
  }
  function layout() {
    try {
      if (mqBar && mqBar.matches) return 'bar';
      if (mqRail && mqRail.matches) return 'rail';
    } catch (e) { /* ignore */ }
    return 'sidebar';
  }
  function screen() {
    try { return html().getAttribute('data-mk-screen') || ''; } catch (e) { return ''; }
  }
  function page() { try { return M.route().page; } catch (e) { return 'other'; } }

  /* ---------------------------------------------------------------- URLs */
  function base() {
    var p = location.pathname, i = p.indexOf('/a/account');
    return i >= 0 ? p.slice(0, i) + '/a/account' : '/a/account';
  }
  function previewQuery() {
    try {
      var cur = new URLSearchParams(location.search), out = new URLSearchParams();
      PREVIEW_KEYS.forEach(function (k) { if (cur.has(k)) out.set(k, cur.get(k)); });
      var s = out.toString();
      return s ? '?' + s : '';
    } catch (e) { return ''; }
  }
  function fullUrl(path, hash) {
    return base() + path + previewQuery() + (hash ? '#' + hash : '');
  }
  function samePath(a, b) {
    return String(a || '').replace(/\/+$/, '') === String(b || '').replace(/\/+$/, '');
  }
  function itemUrl(it) {
    if (it.url) return it.url;
    return fullUrl(it.path, it.hash);
  }
  api.navigate = function (url) { location.assign(url); };
  function go(url) { cancelPending(); screenAfter = null; try { api.navigate(url); } catch (e) { M.log('nav: navigate failed', e && e.message); } }

  /* ---------------------------------------------------------------- build */
  function icon(key) { return M.svg('0 0 24 24', ICON[key] || []); }
  function label(it, longOnly) {
    if (!it.short || longOnly) return M.el('span', { class: 'mk-nav-label' }, it.label);
    return M.el('span', { class: 'mk-nav-label' },
      M.el('span', { class: 'mk-nav-long' }, it.label),
      M.el('span', { class: 'mk-nav-short', 'aria-hidden': 'true' }, it.short));
  }
  function itemNode(it, longOnly) {
    if (it.button) return M.el('button', { type: 'button', class: 'mk-nav-item', 'data-mk-nav-item': it.key }, icon(it.key), label(it, longOnly));
    return M.el('a', { class: 'mk-nav-item', 'data-mk-nav-item': it.key, href: itemUrl(it) }, icon(it.key), label(it, longOnly));
  }
  function mainList() {
    var list = M.el('ul', { class: 'mk-nav-list', role: 'list' });
    ITEMS.forEach(function (it) {
      var li = M.el('li', { 'data-slot': slotOf(it) }, itemNode(it));
      if (it.key === 'logout') li.className = 'mk-nav-out';
      list.appendChild(li);
    });
    var more = M.el('button', { type: 'button', class: 'mk-nav-item mk-nav-more', 'aria-haspopup': 'dialog', 'aria-expanded': 'false', 'aria-controls': 'mk-nav-sheet' },
      icon('more'), M.el('span', { class: 'mk-nav-label' }, 'More'), M.el('span', { class: 'mk-vh', 'data-more-current': '' }));
    list.appendChild(M.el('li', { class: 'mk-nav-more-slot' }, more));
    return list;
  }
  function sheetItemList() {
    var sheetList = M.el('ul', { role: 'list' });
    /* sheet items always show the long label ("Order history", not "History"); an item that can move back to the bar
       (Order history) is in the sheet too, hidden while it is in the bar (refresh) */
    ITEMS.forEach(function (it) {
      if (it.slot !== 'more') return;
      var li = M.el('li', { 'data-sheet-item': it.key, hidden: slotOf(it) !== 'more' }, itemNode(it, true));
      if (it.key === 'logout') li.className = 'mk-nav-out';
      sheetList.appendChild(li);
    });
    return sheetList;
  }
  function build() {
    if (navNode) return navNode;
    var list = mainList();
    var sheetList = sheetItemList();
    var sheet = M.el('div', { id: 'mk-nav-sheet', class: 'mk-nav-sheet', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'mk-nav-sheet-title', hidden: true },
      M.el('div', { class: 'mk-nav-sheet-head' },
        M.el('h2', { id: 'mk-nav-sheet-title' }, 'More'),
        M.el('button', { type: 'button', class: 'mk-nav-sheet-close', 'aria-label': 'Close' }, icon('close'))),
      sheetList);
    var nav = M.el('nav', { class: 'mk-nav', 'aria-label': 'Account' },
      list,
      M.el('div', { class: 'mk-nav-scrim', hidden: true }),
      sheet,
      M.el('p', { class: 'mk-vh', 'aria-live': 'polite', 'data-mk-nav-live': '' }));
    if (NS) {
      nav.setAttribute('data-mk-order', order);
      if (order === 'pending') nav.setAttribute('aria-busy', 'true');
    }
    nav.addEventListener('click', onClick);
    nav.addEventListener('keydown', onKey);
    nav.addEventListener('focusin', onIntent);
    nav.addEventListener('mouseover', onIntent);
    navNode = nav;
    return nav;
  }
  function part(sel) { return navEl ? M.q(sel, navEl) : null; }

  /* ---------------------------------------------------------------- R11: the order (only with the add-on) */
  /* the add-on's answer: 'never' / 'lapsed' = its order; 'subscriber' = the usual; null = unknown (ends a pending frame
     with the usual order, leaves a decided order as it is) */
  function setOrder(kind) {
    if (!NS) return;
    if (kind !== 'subscriber' && !nonsubKind(kind) && order !== 'pending') return;
    stopOrderWait();
    var next = nonsubKind(kind) ? 'nonsub' : 'default';
    var items = next === 'nonsub' ? NONSUB_ITEMS : DEFAULT_ITEMS;
    var changed = items !== ITEMS;
    order = next;
    ITEMS = items;
    if (navNode) {
      if (changed) relist();
      navNode.setAttribute('data-mk-order', order);
      navNode.removeAttribute('aria-busy');
    }
    refresh();
  }
  /* the lists again, for the new order, in the same <nav> (focus stays on the same item where it is still there) */
  function relist() {
    var had = document.activeElement && navNode.contains(document.activeElement) && document.activeElement.getAttribute
      ? document.activeElement.getAttribute('data-mk-nav-item') : null;
    closeSheet(false);
    var oldList = M.q('.mk-nav-list', navNode), oldSheet = M.q('.mk-nav-sheet > ul', navNode);
    if (oldList) oldList.parentNode.replaceChild(mainList(), oldList);
    if (oldSheet) oldSheet.parentNode.replaceChild(sheetItemList(), oldSheet);
    lastActive = null;
    if (had) { var n = M.q('.mk-nav-list [data-mk-nav-item="' + had + '"]', navNode); if (n) try { n.focus(); } catch (e) { /* ignore */ } }
  }
  function stopOrderWait() { if (orderWait) { clearTimeout(orderWait.timer); orderWait = null; } }
  /* pending: ask the add-on, ORDER_WAIT_MS at most (started when the menu is mounted, so never on the login page) */
  function waitOrder() {
    if (!NS || order !== 'pending' || orderWait) return;
    var mine = { timer: setTimeout(function () { if (orderWait === mine) setOrder(null); }, ORDER_WAIT_MS) };
    orderWait = mine;
    var p = null;
    try { p = NS.kind(); } catch (e) { p = null; }
    Promise.resolve(p).then(function (k) { if (orderWait === mine || order !== 'pending') setOrder(k); },
      function () { if (orderWait === mine) setOrder(null); });
  }
  /* Log out (the login page): the next customer of this tab starts pending again */
  function resetOrder() {
    if (!NS) return;
    stopOrderWait();
    var changed = ITEMS !== DEFAULT_ITEMS;
    order = 'pending';
    ITEMS = DEFAULT_ITEMS;
    if (navNode) {
      if (changed) relist();
      navNode.setAttribute('data-mk-order', order);
      navNode.setAttribute('aria-busy', 'true');
    }
  }

  /* ---------------------------------------------------------------- mount / fail closed */
  /* on: ours shows and Skio's is hidden in the same frame; off: Skio's shows. Either way the menu is no
     longer pending (the loader's mk-nav-pending only covers the time before the first answer) */
  function setReadyClass(onOff) {
    try {
      var c = html().classList;
      c[onOff ? 'add' : 'remove']('mk-nav-ready');
      c.remove('mk-nav-pending');
    } catch (e) { /* ignore */ }
  }
  function stopObserver() { if (observer) { try { observer.disconnect(); } catch (e) { /* ignore */ } observer = null; } }
  function teardown(newStatus) {
    setReadyClass(false);           /* first: Skio's menu is back before anything else happens (and no longer pending) */
    screenAfter = null;
    closeSheet(false);
    stopObserver();
    cancelPending();
    waitSeq++;
    var wasMounted = !!(navEl && navEl.isConnected);
    try { M.unmount('mk-nav'); } catch (e) { /* ignore */ }
    navEl = null;
    skioNav = null;
    status = newStatus || 'idle';
    if (wasMounted) dispatch('gone');
  }
  function fail(where, e) {
    M.log('nav: failed (' + where + ')', e && e.message);
    teardown('failed');
  }
  function healthy() {
    return !!(navEl && navEl.isConnected && skioNav && skioNav.isConnected && navEl.parentNode === skioNav.parentNode &&
      M.byName('navbar-logout-button', skioNav));
  }
  function check() {
    try {
      /* Skio changed the page (its push re-renders before the core's route poll): close the sheet now */
      if (sheetOpen && location.pathname !== sheetPath) closeSheet(false);
      if (status !== 'ready' || healthy()) return;
      setReadyClass(false);
      var now = Date.now();
      remounts = remounts.filter(function (t) { return now - t < REMOUNT_WINDOW_MS; });
      remounts.push(now);
      if (remounts.length > MAX_REMOUNTS) { fail('re-mounted too often'); return; }
      status = 'waiting';
      ensure();
    } catch (e) { fail('check', e); }
  }
  function ensure() {
    if (status === 'failed') return;
    if (!PAGES[page()]) { teardown('idle'); return; }
    if (status === 'ready' && healthy()) return;
    status = 'waiting';
    var seq = ++waitSeq;
    var now = M.q('#skio-root [data-skio-name="navbar"]');
    if (now) { attach(now); return; }
    M.waitFor('#skio-root [data-skio-name="navbar"]', { timeout: WAIT_MS }).then(function (nav) {
      if (seq !== waitSeq || status !== 'waiting') return;
      if (!nav) { teardown('idle'); return; }
      attach(nav);
    });
  }
  /* the stylesheet is applied: only it hides Skio's navbar under mk-nav-ready */
  function styled(node) {
    try { return window.getComputedStyle(node).getPropertyValue('--mk-nav-css').trim() === '1'; } catch (e) { return false; }
  }
  function attach(nav) {
    try {
      if (!nav.parentElement || !M.byName('navbar-logout-button', nav)) { teardown('idle'); return; }
      skioNav = nav;
      var node = M.mount({ id: 'mk-nav', anchor: nav, where: 'before', module: 'nav', build: build });
      if (!node || M.qa('.mk-nav-list > li[data-slot] [data-mk-nav-item]', node).length !== ITEMS.length) { fail('mount'); return; }
      if (!styled(node)) { navEl = node; fail('stylesheet missing'); return; }
      navEl = node;
      setReadyClass(true);          /* only now: Skio's navbar hidden, ours shown (same frame) */
      status = 'ready';
      observe();
      refresh();
      waitOrder();
      dispatch('ready');
    } catch (e) { fail('attach', e); }
  }
  function observe() {
    stopObserver();
    var root = document.getElementById('skio-root') || (skioNav && skioNav.parentNode);
    if (!root) return;
    observer = new MutationObserver(check);
    observer.observe(root, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------- current item */
  function active() {
    var scr = screen(), pg = page();
    if (scr === 'support' && itemDef('support')) return { item: 'support', kind: 'page' };
    if (scr === 'upcoming' && pg === 'subscriptions' && usable('upcoming')) return { item: 'upcoming', kind: 'page' };
    if (scr === 'add-products' && pg === 'subscriptions' && usable('add-products')) return { item: 'add-products', kind: 'page' };
    if (RETURN_SCREENS[scr] && pg === 'orders' && itemDef('returns')) return { item: 'returns', kind: 'page' };
    var it = PAGE_ITEM[pg];
    if (!it) return { item: null, kind: null };
    return { item: it, kind: pg === 'subscription' ? 'true' : 'page' };
  }
  function refresh() {
    try {
      if (!navEl) return;
      var a = active(), lay = layout();
      M.qa('[data-mk-nav-item]', navEl).forEach(function (n) { n.removeAttribute('aria-current'); });
      var upLi = M.q('.mk-nav-list [data-mk-nav-item="upcoming"]', navEl);
      if (upLi && upLi.parentNode) upLi.parentNode.hidden = upcomingOff();
      var addLi = M.q('.mk-nav-list [data-mk-nav-item="add-products"]', navEl);
      if (addLi && addLi.parentNode) addLi.parentNode.hidden = catalogHidden();
      /* r10: each item's phone slot now (Order history: sheet while "Add products" is in the bar) */
      ITEMS.forEach(function (it) {
        var n = M.q('.mk-nav-list [data-mk-nav-item="' + it.key + '"]', navEl), sl = slotOf(it);
        if (n && n.parentNode && n.parentNode.getAttribute('data-slot') !== sl) n.parentNode.setAttribute('data-slot', sl);
        var sh = M.q('.mk-nav-sheet [data-sheet-item="' + it.key + '"]', navEl);
        if (sh) sh.hidden = sl !== 'more';
      });
      var more = part('.mk-nav-more');
      var moreText = part('[data-more-current]');
      if (more) more.removeAttribute('data-active');
      if (moreText) M.text(moreText, '');
      var def = a.item ? itemDef(a.item) : null;
      if (def) {
        var inSheet = slotOf(def) === 'more' && lay === 'bar';
        var target = M.q((inSheet ? '.mk-nav-sheet ' : '.mk-nav-list ') + '[data-mk-nav-item="' + a.item + '"]', navEl);
        if (target) target.setAttribute('aria-current', a.kind);
        if (inSheet && more) {
          more.setAttribute('data-active', '');
          if (moreText) M.text(moreText, ' (' + def.label + ')');
        }
      }
      var sig = a.item + ':' + a.kind;
      if (sig !== lastActive) {
        if (announceKey && a.item === announceKey && def) announce(def.label);
        lastActive = sig;
      }
    } catch (e) { M.log('nav: refresh failed', e && e.message); }
  }
  function announce(text) {
    announceKey = null;
    var live = part('[data-mk-nav-live]');
    if (!live) return;
    M.text(live, '');
    setTimeout(function () { M.text(live, text); }, 60);
  }

  /* ---------------------------------------------------------------- navigation */
  function markPending(key, target) {
    var n = M.q('.mk-nav-list [data-mk-nav-item="' + key + '"]', navEl);
    if (n) { n.setAttribute('data-pending', ''); n.setAttribute('aria-busy', 'true'); }
    pending = { key: key, target: target, el: n, timer: null };
  }
  function cancelPending() {
    if (!pending) return;
    if (pending.timer) clearTimeout(pending.timer);
    if (pending.el) { pending.el.removeAttribute('data-pending'); pending.el.removeAttribute('aria-busy'); }
    pending = null;
  }
  /* press Skio's own (hidden) control; full load if the URL has not reached the target in 4 s */
  function native(name, key, path, fallbackUrl) {
    cancelPending();
    var url = fallbackUrl || fullUrl(path);
    var scope = name === 'loyalty-link-banner' ? document.getElementById('skio-root') : skioNav;
    var x = scope ? M.byName(name, scope) : null;
    if (!x) { go(url); return 'full'; }
    var target = base() + path;
    markPending(key, target);
    var mine = pending;
    try { x.click(); } catch (e) { go(url); return 'full'; }
    if (pending !== mine) return 'native';
    if (samePath(location.pathname, target)) { cancelPending(); return 'native'; }
    mine.timer = setTimeout(function () {
      if (pending !== mine) return;
      var reached = samePath(location.pathname, target);
      cancelPending();
      if (!reached) go(url);
    }, FALLBACK_MS);
    return 'native';
  }
  function previousPath() {
    try { return window.sessionStorage.getItem('_skioPreviousPath'); } catch (e) { return undefined; }
  }
  /* Skio's own control that brings this page to the start page (/subscriptions) client-side, or null:
     Back on a subscription page always does; on the other pages Back goes to Skio's previous page, which
     is the start page only when there is none (null) or it was the start page */
  function toStartControl(pg) {
    if (pg === 'subscription') return 'navbar-back-button';
    var prev = previousPath();
    if (prev === undefined) return null;
    if (prev === null || prev === '/subscriptions') return 'navbar-back-button';
    return null;
  }
  /* a home screen ('upcoming' / 'add-products') on the start page: the home module's own opener (Next's state on the
     new entry); location.hash only when the opener is missing or refuses */
  function openScreenHere(name) {
    var H = window.MKPortalHome, fn = HOME_SCREENS[name] && H ? H[HOME_SCREENS[name].open] : null;
    if (typeof fn === 'function') { try { if (fn.call(H)) return; } catch (e) { /* fall through */ } }
    location.hash = name;
  }
  /* r10: read the catalogue on intent (focus on the item, the mouse over it); the home module caches the answer */
  function warmCatalog() {
    var t = Date.now();
    if (t - warmAt < WARM_MIN_MS || !usable('add-products')) return false;
    var H = window.MKPortalHome;
    if (!H || typeof H.prefetchCatalog !== 'function') return false;
    warmAt = t;
    try { H.prefetchCatalog(); } catch (e) { M.log('nav: prefetch failed', e && e.message); }
    return true;
  }
  function onIntent(ev) {
    try {
      var a = ev.target && ev.target.closest ? ev.target.closest('[data-mk-nav-item="add-products"]') : null;
      if (a) warmCatalog();
    } catch (e) { /* ignore */ }
  }
  function toTop() {
    var reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { /* ignore */ }
    try { window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' }); } catch (e) { window.scrollTo(0, 0); }
  }
  /* R11: the returns module's own opener (its #returns history entry); location.hash only when it refuses */
  function openReturns() {
    var R = window.MKPortalReturns;
    if (R && typeof R.open === 'function') { try { if (R.open() === true) return; } catch (e) { /* fall through */ } }
    location.hash = 'returns';
  }
  /* the page's own item while a screen is open: the screen's owner closes it */
  function closeScreen(scr, it) {
    var owner = HOME_SCREENS[scr] ? window.MKPortalHome : scr === 'support' ? window.MKPortalSupport :
      RETURN_SCREENS[scr] ? window.MKPortalReturns : null;
    var fn = owner && (HOME_SCREENS[scr] ? owner[HOME_SCREENS[scr].close] : owner.close);
    if (typeof fn === 'function') {
      try { fn.call(owner); return 'close'; } catch (e) { /* fall through */ }
    }
    go(itemUrl(it));
    return 'full';
  }
  function act(key) {
    var it = itemDef(key);
    if (!it || !usable(key)) return 'none';
    var pg = page(), scr = screen();
    if (key === 'logout') return logout();
    if (key === 'support') {
      if (window.MKPortalSupport) { if (scr !== 'support') location.hash = 'support'; return 'hash'; }
      return 'link';
    }
    /* R11: Returns, a screen over Order history (the returns module's) */
    if (key === 'returns') {
      if (pg === 'orders') {
        if (scr === 'returns') { toTop(); return 'current'; }
        openReturns();
        return 'hash';
      }
      screenAfter = { screen: 'returns', page: 'orders', at: Date.now() };
      var viaOrders = native('navbar-orders-button', key, '/orders', itemUrl(it));
      if (viaOrders !== 'native') screenAfter = null;
      return viaOrders;
    }
    /* current page (the item's own page) */
    var ownPage = ITEM_PAGE[key] === pg;
    if (HOME_SCREENS[key]) {
      if (pg === 'subscriptions') {
        if (scr === key) return 'hash';
        openScreenHere(key);
        return 'hash';
      }
      /* another page: Skio's own button to the start page where there is one, then the screen there */
      var ctl = toStartControl(pg);
      if (ctl) {
        screenAfter = { screen: key, at: Date.now() };
        var how = native(ctl, key, '/subscriptions', itemUrl(it));
        if (how !== 'native') screenAfter = null;
        return how;
      }
      go(itemUrl(it));
      return 'full';
    }
    if (ownPage) {
      if (scr) return closeScreen(scr, it);
      toTop();
      return 'current';
    }
    if (key === 'subscriptions') {
      var back = toStartControl(pg);
      if (back) return native(back, key, it.path);
      go(itemUrl(it));
      return 'full';
    }
    if (key === 'orders') return native('navbar-orders-button', key, it.path);
    if (key === 'account') return native('navbar-profile-button', key, it.path);
    if (key === 'rewards') {
      if (pg === 'subscriptions') return native('loyalty-link-banner', key, it.path);
      go(itemUrl(it));
      return 'full';
    }
    return 'none';
  }
  function logout() {
    var now = Date.now();
    if (now - logoutAt < LOGOUT_GUARD_MS) return 'ignored';
    var b = (skioNav && M.byName('navbar-logout-button', skioNav)) || M.byName('navbar-logout-button', document.getElementById('skio-root'));
    if (!b) {
      /* never imitate a logout: show Skio's own menu instead */
      teardown('failed');
      return 'missing';
    }
    logoutAt = now;
    try { b.click(); } catch (e) { teardown('failed'); return 'missing'; }
    return 'pressed';
  }

  function onClick(ev) {
    try {
      var t = ev.target && ev.target.closest ? ev.target : null;
      if (!t) return;
      if (t.closest('.mk-nav-scrim') || t.closest('.mk-nav-sheet-close')) { closeSheet(true); return; }
      if (t.closest('.mk-nav-more')) { if (sheetOpen) closeSheet(true); else openSheet(); return; }
      var a = t.closest('[data-mk-nav-item]');
      if (!a) return;
      var key = a.getAttribute('data-mk-nav-item');
      var isLink = a.tagName === 'A';
      if (isLink && (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey)) return;
      var fromSheet = !!a.closest('.mk-nav-sheet');
      if (fromSheet) closeSheet(false);
      if (key === 'support' && !window.MKPortalSupport) return;   /* plain link, same tab */
      if (isLink) ev.preventDefault();
      announceKey = key;
      var how = act(key);
      if (how !== 'native' && how !== 'hash' && how !== 'close') announceKey = null;
      if (fromSheet && (how === 'current' || how === 'ignored')) focusMore();
    } catch (e) {
      /* the link's own href still works: let the browser follow it */
      M.log('nav: click failed', e && e.message);
    }
  }

  /* ---------------------------------------------------------------- "More" sheet (phones) */
  /* the sheet's items shown now (r10: Order history is hidden there while it is in the bar) */
  function sheetItems(sheet) { return M.qa('[data-mk-nav-item]', sheet).filter(function (n) { return !n.closest('[hidden]'); }); }
  function focusMore() { var m = part('.mk-nav-more'); if (m && m.isConnected) try { m.focus(); } catch (e) { /* ignore */ } }
  function setInert(sheet) {
    inerted = [];
    var keep = [part('.mk-nav-scrim'), part('[data-mk-nav-live]')];
    var n = sheet;
    while (n && n.parentElement && n !== document.body) {
      var p = n.parentElement;
      Array.prototype.forEach.call(p.children, function (c) {
        if (c === n || keep.indexOf(c) >= 0 || c.hasAttribute('inert') || /^(SCRIPT|STYLE|LINK|META|TEMPLATE)$/.test(c.tagName)) return;
        c.setAttribute('inert', '');
        inerted.push(c);
      });
      if (p === document.body) break;
      n = p;
    }
  }
  function openSheet() {
    if (sheetOpen || layout() !== 'bar' || !navEl) return;
    try {
      var sheet = part('.mk-nav-sheet'), scrim = part('.mk-nav-scrim'), more = part('.mk-nav-more');
      if (!sheet || !scrim || !more) return;
      sheet.hidden = false;
      scrim.hidden = false;
      more.setAttribute('aria-expanded', 'true');
      html().classList.add('mk-nav-sheet-open');
      sheetOpen = true;
      sheetPath = location.pathname;
      setInert(sheet);
      var first = sheetItems(sheet)[0];
      if (first) first.focus();
    } catch (e) {
      M.log('nav: sheet failed', e && e.message);
      closeSheet(false);
    }
  }
  function closeSheet(restoreFocus) {
    if (!sheetOpen) return;
    sheetOpen = false;
    try {
      inerted.forEach(function (c) { try { c.removeAttribute('inert'); } catch (e) { /* ignore */ } });
      inerted = [];
      html().classList.remove('mk-nav-sheet-open');
      var sheet = part('.mk-nav-sheet'), scrim = part('.mk-nav-scrim'), more = part('.mk-nav-more');
      if (sheet) sheet.hidden = true;
      if (scrim) scrim.hidden = true;
      if (more) more.setAttribute('aria-expanded', 'false');
      if (restoreFocus) focusMore();
    } catch (e) { /* ignore */ }
  }
  function onKey(ev) {
    try {
      if (!sheetOpen) return;
      if (ev.key === 'Escape' || ev.key === 'Esc') { ev.preventDefault(); closeSheet(true); return; }
      if (ev.key !== 'Tab') return;
      var sheet = part('.mk-nav-sheet');
      var f = M.qa('a[href], button:not([disabled])', sheet).filter(function (n) { return !n.closest('[hidden]'); });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
    } catch (e) { /* ignore */ }
  }
  /* focus that escapes the sheet (inert normally prevents it): bring it back */
  function onFocusIn(ev) {
    try {
      if (!sheetOpen) return;
      var sheet = part('.mk-nav-sheet');
      if (sheet && ev.target && !sheet.contains(ev.target)) {
        var first = sheetItems(sheet)[0];
        if (first) first.focus();
      }
    } catch (e) { /* ignore */ }
  }

  /* ---------------------------------------------------------------- phone keyboard */
  function kbCheck() {
    try {
      var a = document.activeElement, vv = window.visualViewport;
      var typing = !!(a && a.matches && a.matches('input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, select, [contenteditable="true"]') &&
        a.closest('#skio-root, #skio-portal'));
      var small = !!(vv && vv.height < 0.75 * window.innerHeight);
      html().classList[layout() === 'bar' && typing && small ? 'add' : 'remove']('mk-kb-open');
    } catch (e) { /* ignore */ }
  }

  /* ---------------------------------------------------------------- start */
  function onRoute(r) {
    try {
      closeSheet(false);
      remounts = [];
      if (status === 'failed') status = 'idle';     /* a new page view may try again */
      /* any route change ends a pending native navigation (no late full load to an abandoned target);
         announce only when it reached its own target */
      if (pending) {
        var hit = samePath(location.pathname, pending.target), k = pending.key;
        cancelPending();
        if (hit) announceKey = k;
      }
      /* Upcoming chosen on another page: the start page is here now; open the screen after every other
         route listener ran (the home module starts its page on the same announcement) */
      var want = screenAfter;
      screenAfter = null;
      if (want && r && r.page === (want.page || 'subscriptions') && Date.now() - want.at <= FALLBACK_MS && usable(want.screen)) {
        announceKey = want.screen;
        setTimeout(function () {
          try {
            if (page() !== (want.page || 'subscriptions') || screen() === want.screen || !usable(want.screen)) return;
            if (want.screen === 'returns') openReturns(); else openScreenHere(want.screen);
          } catch (e) { M.log('nav: screen after route failed', e && e.message); }
        }, 0);
      }
      if (r && r.page === 'login') resetOrder();
      if (!r || !PAGES[r.page]) { teardown('idle'); return; }
      ensure();
      refresh();
    } catch (e) { fail('route', e); }
  }
  function start() {
    try {
      mqBar = window.matchMedia('(max-width: 767.98px)');
      mqRail = window.matchMedia('(max-width: 1023.98px)');
      var onMq = function () { if (layout() !== 'bar') closeSheet(false); refresh(); kbCheck(); };
      if (mqBar.addEventListener) { mqBar.addEventListener('change', onMq); mqRail.addEventListener('change', onMq); }
      else if (mqBar.addListener) { mqBar.addListener(onMq); mqRail.addListener(onMq); }
    } catch (e) { /* layout stays 'sidebar' */ }
    window.addEventListener('hashchange', function () { closeSheet(false); refresh(); });
    window.addEventListener('popstate', function () { refresh(); });
    window.addEventListener('pageshow', function () { refresh(); });
    window.addEventListener('pagehide', function () { closeSheet(false); });
    var onScreen = function () { refresh(); };
    window.addEventListener('mk-portal:screen', onScreen);
    document.addEventListener('mk-portal:screen', onScreen);
    document.addEventListener('focusin', function (ev) { onFocusIn(ev); kbCheck(); });
    document.addEventListener('focusout', function () { setTimeout(kbCheck, 0); });
    try { if (window.visualViewport) window.visualViewport.addEventListener('resize', kbCheck); } catch (e) { /* ignore */ }
    /* R11: the add-on's answers on later pages (classify() runs on every page; a change sets the order) */
    if (NS) document.addEventListener('mk-portal:nonsub', function (ev) { try { setOrder(ev && ev.detail ? ev.detail.kind : null); } catch (e) { M.log('nav: order failed', e && e.message); } });
    /* r10: the home module (loaded after this file) may switch its screens off; look again once every script ran */
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { refresh(); });
    M.onRoute(onRoute);
  }

  api.version = VERSION;
  api.state = function () {
    return {
      status: status,
      active: active().item,
      items: ITEMS.map(function (it) { return it.key; }),
      layout: layout(),
      sheetOpen: sheetOpen,
      pending: (function () { try { return html().classList.contains('mk-nav-pending'); } catch (e) { return false; } })(),
      bar: ITEMS.filter(function (it) { return slotOf(it) === 'bar' && usable(it.key); }).map(function (it) { return it.key; }),
      sheet: ITEMS.filter(function (it) { return slotOf(it) === 'more' && usable(it.key); }).map(function (it) { return it.key; }),
      order: order
    };
  };
  api.refresh = function () { refresh(); };
  /* test hook: what a menu choice does on the current page ('native' | 'full' | 'hash' | 'link' | 'current' | 'close' | ...) */
  api.__act = function (key) { return act(key); };
  window.MKPortalNav = api;
  try { start(); } catch (e) { fail('start', e); }
})();
